import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useAuthStore } from '../state/auth';
import {
  getStoredActiveOrgId,
  LicenseState,
  ModuleAccess,
  OrgInfo,
  PublicSiteAccess,
  setStoredActiveOrgId,
  useTenantStore,
} from '../state/tenant';
import { api, getApiSessionToken, setApiSessionToken } from '../lib/api';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';

const SESSION_CHECK_INTERVAL_MS = 60_000;
const SESSION_CHECK_MIN_GAP_MS = 5_000;

type Props = { children: ReactNode };

type RawTenantOrg = {
  id?: number | string | null;
  [key: string]: unknown;
};

type TenantPayloadInput = {
  organizations?: unknown;
  orgs?: unknown;
  activeOrganizationId?: unknown;
  active_organization_id?: unknown;
  role?: unknown;
  activeRole?: unknown;
  scopes?: unknown;
  user?: unknown;
  session_token?: unknown;
  features?: unknown;
  license?: unknown;
  publicSiteAccess?: unknown;
  moduleAccess?: unknown;
  usesLegacyAccessFallback?: unknown;
};

type ApiErrorLike = {
  response?: {
    status?: number;
    data?: {
      code?: string;
      error?: string;
    };
  };
};

function extractTenantPayload(data: TenantPayloadInput | null | undefined) {
  const rawOrganizations: RawTenantOrg[] = Array.isArray(data?.organizations)
    ? (data.organizations as RawTenantOrg[])
    : Array.isArray(data?.orgs)
      ? (data.orgs as RawTenantOrg[])
      : [];
  const organizations = rawOrganizations.flatMap<OrgInfo>((org) => {
    const id = typeof org.id === 'number' ? org.id : Number(org.id);
    const nombre = typeof org.nombre === 'string' ? org.nombre : '';
    const role =
      org.role === 'admin' || org.role === 'editor' || org.role === 'viewer' ? org.role : null;
    const status = typeof org.status === 'string' ? org.status : '';
    if (!Number.isFinite(id) || !nombre || !role || !status) return [];
    return [
      {
        id,
        nombre,
        role,
        is_owner: Boolean(org.is_owner),
        status,
        logo_url: typeof org.logo_url === 'string' || org.logo_url === null ? org.logo_url : null,
        moneda_locale:
          typeof org.moneda_locale === 'string' || org.moneda_locale === null
            ? org.moneda_locale
            : null,
        moneda_formato:
          org.moneda_formato && typeof org.moneda_formato === 'object'
            ? (org.moneda_formato as OrgInfo['moneda_formato'])
            : null,
        moneda_base:
          org.moneda_base && typeof org.moneda_base === 'object'
            ? (org.moneda_base as OrgInfo['moneda_base'])
            : null,
      },
    ];
  });

  const rawActiveOrgId = data?.activeOrganizationId ?? data?.active_organization_id;
  const parsedActiveOrgId =
    typeof rawActiveOrgId === 'number'
      ? rawActiveOrgId
      : typeof rawActiveOrgId === 'string'
        ? Number(rawActiveOrgId)
        : NaN;
  const activeOrganizationId = Number.isFinite(parsedActiveOrgId) ? parsedActiveOrgId : null;
  const role = (data?.role ?? data?.activeRole ?? null) as 'admin' | 'editor' | 'viewer' | null;
  const scopes = Array.isArray(data?.scopes) ? data.scopes : [];
  const features = Array.isArray(data?.features)
    ? data.features.filter((item): item is string => typeof item === 'string')
    : [];
  const rawLicense =
    data?.license && typeof data.license === 'object'
      ? (data.license as Record<string, unknown>)
      : null;
  const license: LicenseState | null = rawLicense
    ? {
        max_usuarios: typeof rawLicense.max_usuarios === 'number' ? rawLicense.max_usuarios : null,
        active_users: typeof rawLicense.active_users === 'number' ? rawLicense.active_users : 0,
        pending_invitations:
          typeof rawLicense.pending_invitations === 'number' ? rawLicense.pending_invitations : 0,
        seats_available:
          typeof rawLicense.seats_available === 'number' ? rawLicense.seats_available : null,
        operational: rawLicense.operational !== false,
      }
    : null;
  const dbUser = (data?.user as Record<string, unknown> | null | undefined) ?? null;
  const sessionToken = typeof data?.session_token === 'string' ? data.session_token : null;
  const moduleAccess = Array.isArray(data?.moduleAccess)
    ? data.moduleAccess.flatMap<ModuleAccess>((item) => {
        if (!item || typeof item !== 'object') return [];
        const record = item as Record<string, unknown>;
        const role =
          record.role === 'viewer' || record.role === 'operator' || record.role === 'supervisor'
            ? record.role
            : null;
        if (typeof record.moduleKey !== 'string' || !role) return [];
        return [
          {
            moduleKey: record.moduleKey,
            role,
            capabilities: Array.isArray(record.capabilities)
              ? record.capabilities.filter(
                  (capability): capability is string => typeof capability === 'string',
                )
              : [],
          },
        ];
      })
    : [];
  const usesLegacyAccessFallback = data?.usesLegacyAccessFallback === true;
  const rawPublicSiteAccess =
    data?.publicSiteAccess && typeof data.publicSiteAccess === 'object'
      ? (data.publicSiteAccess as Record<string, unknown>)
      : null;
  const publicSiteRole: PublicSiteAccess['role'] =
    rawPublicSiteAccess?.role === 'superadmin' ||
    rawPublicSiteAccess?.role === 'editor' ||
    rawPublicSiteAccess?.role === 'publisher'
      ? rawPublicSiteAccess.role
      : null;
  const publicSiteAccess: PublicSiteAccess | null = rawPublicSiteAccess
    ? {
        role: publicSiteRole,
        can_read: rawPublicSiteAccess.can_read === true,
        can_write: rawPublicSiteAccess.can_write === true,
        can_publish: rawPublicSiteAccess.can_publish === true,
        can_media: rawPublicSiteAccess.can_media === true,
        can_manage_editors: rawPublicSiteAccess.can_manage_editors === true,
      }
    : null;
  return {
    organizations,
    activeOrganizationId,
    role,
    scopes,
    features,
    license,
    dbUser,
    sessionToken,
    moduleAccess,
    usesLegacyAccessFallback,
    publicSiteAccess,
  };
}

function isPublicRoute(pathname: string) {
  return (
    pathname === '/' ||
    pathname === '/login' ||
    pathname === '/invitations/accept' ||
    pathname === '/ownership-transfer/respond' ||
    pathname === '/blog' ||
    pathname.startsWith('/blog/')
  );
}

export function AuthBoundary({ children }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser, loading, setLoading } = useAuthStore();
  const { setTenant, setClaims } = useTenantStore();
  const [tenantLoading, setTenantLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [sessionConflictOpen, setSessionConflictOpen] = useState(false);
  const sessionConflictResolveRef = useRef<((continueHere: boolean) => void) | null>(null);

  // Reemplaza a window.confirm(): algunos contextos de navegador embebidos (ej. el "Simple
  // Browser" de VS Code) no soportan dialogos bloqueantes nativos y resuelven confirm() de
  // inmediato como cancelado, cerrando la sesion en silencio sin que el usuario vea nada. Un
  // modal propio funciona igual en cualquier contexto.
  function confirmSessionOverride(): Promise<boolean> {
    return new Promise((resolve) => {
      sessionConflictResolveRef.current = resolve;
      setSessionConflictOpen(true);
    });
  }

  function resolveSessionConflict(continueHere: boolean) {
    setSessionConflictOpen(false);
    sessionConflictResolveRef.current?.(continueHere);
    sessionConflictResolveRef.current = null;
  }

  // Se lee dentro del callback de onAuthStateChanged via ref (no como dependencia del effect):
  // ese callback dispara de inmediato en cada nueva suscripcion, asi que si `location` estuviera
  // en el arreglo de dependencias, CADA cambio de ruta (navegar a cualquier pantalla, no solo
  // login/logout real) volvia a correr todo el bootstrap de sesion/tenant -- incluida la
  // validacion/emision de `x-session-token` -- de forma redundante en cada navegacion, compitiendo
  // con las llamadas autenticadas que la pantalla de destino dispara al montar (ej. PublicSiteAdmin).
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        setTenantLoading(true);
        setErrorMessage(null);
        let tenantLoadedSuccessfully = false;
        const existingSuperadmin = useAuthStore.getState().user?.isSuperadmin;
        setUser({
          uid: fbUser.uid,
          email: fbUser.email,
          displayName: fbUser.displayName,
          photoURL: fbUser.photoURL,
          isSuperadmin: Boolean(existingSuperadmin),
        });
        try {
          const tokenResult = await fbUser.getIdTokenResult();
          const token = tokenResult.token;
          const claims = {
            org_id: tokenResult.claims.org_id as number | undefined,
            role: tokenResult.claims.role as string | undefined,
            scopes: tokenResult.claims.scopes as string[] | undefined,
            superadmin: tokenResult.claims.superadmin as boolean | undefined,
          };
          setClaims(claims);
          setUser({
            uid: fbUser.uid,
            email: fbUser.email,
            displayName: fbUser.displayName,
            photoURL: fbUser.photoURL,
            isSuperadmin: Boolean(claims.superadmin || existingSuperadmin),
          });
          const pendingInvite = sessionStorage.getItem('pendingInviteToken');
          let invitedOrgId: number | null = null;
          if (pendingInvite) {
            try {
              const accepted = await api.post('/api/invitations/accept', { token: pendingInvite });
              const rawOrgId = accepted.data?.organization_id;
              invitedOrgId = typeof rawOrgId === 'number' ? rawOrgId : Number(rawOrgId);
              if (!Number.isFinite(invitedOrgId)) invitedOrgId = null;
              sessionStorage.removeItem('pendingInviteToken');
            } catch (acceptErr) {
              sessionStorage.removeItem('pendingInviteToken');
              // eslint-disable-next-line no-console
              console.error('Error al aceptar invitación pendiente', acceptErr);
            }
          }
          const getTenantSession = async (forceSession: boolean, extraParams = {}) =>
            api.get('/api/auth/me', {
              params: { _ts: Date.now(), ...extraParams },
              headers: {
                Authorization: `Bearer ${token}`,
                ...(invitedOrgId || getStoredActiveOrgId()
                  ? { 'x-org-id': invitedOrgId ?? getStoredActiveOrgId() }
                  : {}),
                ...(forceSession ? { 'x-force-session': 'true' } : {}),
              },
            });

          let forcedSession = false;
          let res;
          try {
            res = await getTenantSession(false);
          } catch (err) {
            const apiErr = err as ApiErrorLike;
            const isActiveElsewhere =
              apiErr?.response?.status === 409 &&
              apiErr.response.data?.code === 'SESSION_ACTIVE_ELSEWHERE';
            if (!isActiveElsewhere) {
              throw err;
            }

            const shouldContinue = await confirmSessionOverride();
            if (!shouldContinue) {
              setApiSessionToken(null);
              await logout();
              setLoading(false);
              return;
            }

            forcedSession = true;
            res = await getTenantSession(true);
          }
          setApiSessionToken(res.data?.session_token);
          let tenantData = extractTenantPayload(res.data);
          if (tenantData.organizations.length === 0) {
            const retry = await api.get('/api/auth/me', {
              params: { _ts: Date.now(), _retry: Math.random() },
              headers: {
                Authorization: `Bearer ${token}`,
                ...(invitedOrgId || getStoredActiveOrgId()
                  ? { 'x-org-id': invitedOrgId ?? getStoredActiveOrgId() }
                  : {}),
                ...(forcedSession ? { 'x-force-session': 'true' } : {}),
                'Cache-Control': 'no-store',
                Pragma: 'no-cache',
              },
            });
            setApiSessionToken(retry.data?.session_token);
            tenantData = extractTenantPayload(retry.data);
          }
          if (tenantData.organizations.length === 0) {
            throw new Error('La respuesta de sesión no incluyó una organización válida.');
          }
          const {
            organizations,
            activeOrganizationId,
            role,
            scopes,
            features,
            license,
            dbUser,
            publicSiteAccess,
            moduleAccess,
            usesLegacyAccessFallback,
          } = tenantData;
          setTenant(
            organizations,
            activeOrganizationId,
            role,
            scopes ?? [],
            features,
            license,
            publicSiteAccess,
            moduleAccess,
            usesLegacyAccessFallback,
          );
          const rawAvatar = Object.prototype.hasOwnProperty.call(dbUser ?? {}, 'avatar')
            ? dbUser?.avatar
            : undefined;
          const avatarFromDb =
            typeof rawAvatar === 'string' || rawAvatar === null ? rawAvatar : undefined;
          setUser({
            uid: fbUser.uid,
            email: fbUser.email,
            displayName: fbUser.displayName,
            photoURL: avatarFromDb !== undefined ? avatarFromDb : fbUser.photoURL,
            isSuperadmin: Boolean(dbUser?.is_superadmin || claims.superadmin),
          });
          tenantLoadedSuccessfully = true;
          if (role === 'admin') {
            const onboardingRes = await api.get('/api/onboarding', {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (
              !onboardingRes.data?.onboarding_completed &&
              locationRef.current.pathname !== '/onboarding'
            ) {
              navigate('/onboarding', { replace: true });
              setLoading(false);
              setTenantLoading(false);
              return;
            }
          }
          setErrorMessage(null);
        } catch (err) {
          const apiErr = err as ApiErrorLike;
          if (apiErr?.response?.status === 503) {
            setErrorMessage('No hay conexión con la base de datos. Intenta más tarde.');
          } else if (apiErr?.response?.status === 403) {
            setErrorMessage(
              'Tu usuario no tiene una organización activa. Revisa membresías de tu cuenta.',
            );
          } else if (apiErr?.response?.status === 401) {
            setErrorMessage(
              'Tu sesión expiró o no se pudo validar. Cierra sesión e inicia nuevamente.',
            );
          } else {
            setErrorMessage(
              'No se pudo cargar la organización. Verifica tu conexión e intenta nuevamente.',
            );
          }
          // eslint-disable-next-line no-console
          console.error('Error al cargar tenant', err);
        }
        setLoading(false);
        setTenantLoading(false);
        if (tenantLoadedSuccessfully && locationRef.current.pathname === '/login') {
          const rawFrom =
            typeof (locationRef.current.state as { from?: unknown } | null)?.from === 'string'
              ? ((locationRef.current.state as { from?: string }).from ?? '')
              : '';
          const safeFrom =
            rawFrom.startsWith('/') && !rawFrom.startsWith('/login') ? rawFrom : '/tesoreria';
          navigate(safeFrom, { replace: true });
        }
      } else {
        setUser(null);
        setApiSessionToken(null);
        setStoredActiveOrgId(null);
        setTenant([], null, null, []);
        setClaims(null);
        setLoading(false);
        setTenantLoading(false);
        setErrorMessage(null);
        if (!isPublicRoute(locationRef.current.pathname)) {
          const from = `${locationRef.current.pathname}${locationRef.current.search}${locationRef.current.hash}`;
          navigate('/login', { replace: true, state: { from } });
        }
      }
    });
    return () => unsub();
    // location deliberadamente fuera de las dependencias -- ver comentario en locationRef arriba.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setUser, setLoading, setTenant, setClaims, navigate, retryNonce]);

  // Sesion unica: detecta si la sesion se abrio en otro equipo mientras esta pestaña esta quieta
  // (cualquier accion real ya se valida en el servidor). Solo con la pestaña visible, cada minuto,
  // y enseguida al volver a ella; un 401 lo maneja el interceptor de lib/api.ts.
  useEffect(() => {
    if (!user || !getApiSessionToken()) return undefined;

    let lastCheck = Date.now();
    const check = () => {
      if (!getApiSessionToken() || document.visibilityState !== 'visible') return;
      lastCheck = Date.now();
      api.get('/api/auth/session/status').catch(() => undefined);
    };
    const onReturn = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastCheck > SESSION_CHECK_MIN_GAP_MS) check();
    };

    const intervalId = window.setInterval(check, SESSION_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [user]);

  const sessionConflictModal = (
    <Modal
      open={sessionConflictOpen}
      title="Sesión activa en otro dispositivo"
      onClose={() => resolveSessionConflict(false)}
      footer={
        <>
          <Button variant="outline" onClick={() => resolveSessionConflict(false)}>
            Cancelar
          </Button>
          <Button onClick={() => resolveSessionConflict(true)}>Continuar aquí</Button>
        </>
      }
    >
      <p>
        Este usuario tiene sesión iniciada en otro dispositivo. ¿Deseas cerrar las sesiones en los
        otros dispositivos y continuar en esta?
      </p>
    </Modal>
  );

  if (loading || tenantLoading) {
    return (
      <>
        <div className="loading-screen">
          <div className="loading-card">
            <span className="pill">Preparando sesión</span>
            <h2 className="text-xl" style={{ margin: '10px 0 6px' }}>
              Estamos sincronizando tu acceso
            </h2>
            <p className="page-subtitle">
              {errorMessage ?? 'Esto puede tardar unos segundos la primera vez.'}
            </p>
          </div>
        </div>
        {sessionConflictModal}
      </>
    );
  }

  if (user && errorMessage) {
    return (
      <>
        <div className="loading-screen">
          <div className="loading-card tenant-load-error" role="alert">
            <span className="pill">No pudimos preparar tu sesión</span>
            <h2 className="text-xl" style={{ margin: '10px 0 6px' }}>
              Organización no disponible
            </h2>
            <p className="page-subtitle">{errorMessage}</p>
            <div className="tenant-load-error-actions">
              <Button
                type="button"
                onClick={() => {
                  setTenantLoading(true);
                  setRetryNonce((current) => current + 1);
                }}
              >
                Reintentar
              </Button>
              <Button type="button" variant="outline" onClick={() => void logout()}>
                Cerrar sesión
              </Button>
            </div>
          </div>
        </div>
        {sessionConflictModal}
      </>
    );
  }

  // Permitir login sin sesión; bloquear rutas protegidas
  if (!user && !isPublicRoute(location.pathname)) {
    return sessionConflictModal;
  }

  return (
    <>
      {children}
      {sessionConflictModal}
    </>
  );
}

export async function logout() {
  setApiSessionToken(null);
  await signOut(auth);
}
